import { type FC, useCallback, useEffect, useState } from "react";
import { DDSButton } from "@web-monorepo/dds";
import { autoTranslate } from "@web-monorepo/translation-runtime";
import env from "@web-monorepo/shared/utils/env";

const COUNTDOWN_SECONDS = env.isPlaywright ? 1 : 30;
const SECONDS_TO_SHOW_CODE_SENT = 3;

const useCountdown = () => {
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const startCountdown = useCallback(() => setCountdown(COUNTDOWN_SECONDS + SECONDS_TO_SHOW_CODE_SENT), [setCountdown]);

  useEffect(() => {
    if (!countdown) return;

    const intervalId = setTimeout(() => {
      // eslint-disable-next-line @web-monorepo/no-setState-in-useEffect -- legitimate timer pattern: setTimeout drives countdown state each second
      setCountdown((c) => c - 1);
    }, 1000);

    return () => clearTimeout(intervalId);
  }, [countdown, setCountdown]);

  return [countdown, startCountdown] as const;
};

type ButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

type Props = Pick<ButtonProps, "onClick">;

const getText = (countdown: number): string => {
  if (countdown > COUNTDOWN_SECONDS) {
    return autoTranslate("Code sent");
  }
  if (countdown > 0) {
    return autoTranslate("Resend code (__seconds__)", { seconds: countdown.toFixed() });
  }

  return autoTranslate("Resend code");
};

const ResendCodeButton: FC<Props> = ({ onClick }) => {
  const [countdown, startCountdown] = useCountdown();

  return (
    <DDSButton
      variant="tertiary"
      size="large"
      data-name="otp_entry.resend_code"
      disabled={countdown > 0}
      onClick={(event) => {
        startCountdown();
        onClick?.(event);
      }}
    >
      {getText(countdown)}
    </DDSButton>
  );
};

export default ResendCodeButton;
